import React, { useState } from 'react';
import { Select, Button, Checkbox, Tag } from 'antd';
import {
  ExclamationCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  MessageOutlined,
  ClockCircleOutlined,
  MailOutlined,
} from '@ant-design/icons';
import './Issues.scss';

const { Option } = Select;

// Reusable Issue Summary Card Component
const IssueSummaryCard = ({ title, count, subtitle, variant = 'default' }) => {
  const getIndicatorClass = () => {
    switch (variant) {
      case 'critical': return 'indicator-critical';
      case 'warning': return 'indicator-warning';
      case 'info': return 'indicator-info';
      default: return 'indicator-default';
    }
  };

  return (
    <div className="issue-summary-card">
      <div className="card-header">
        <h4 className="card-title">{title}</h4>
        <span className={`status-indicator ${getIndicatorClass()}`} />
      </div>
      <div className="card-count">{count}</div>
      <p className="card-subtitle">{subtitle}</p>
    </div>
  );
};

// Reusable AI Suggestion Component
const AISuggestion = ({ confidence, message, onUpdate, onDismiss }) => {
  return (
    <div className="ai-suggestion-banner">
      <div className="ai-icon">
        <MessageOutlined />
      </div>
      <div className="ai-content">
        <h3 className="ai-title">AI-Powered Suggestion ({confidence}% confidence)</h3>
        <p className="ai-message" dangerouslySetInnerHTML={{ __html: message }} />
        <div className="ai-actions">
          <Button type="default" size="large" onClick={onUpdate}>
            Update Contact
          </Button>
          <Button type="text" size="large" onClick={onDismiss}>
            Dismiss
          </Button>
        </div>
      </div>
    </div>
  );
};

// Reusable Issue Item Component
const IssueItem = ({ 
  issueId, 
  bookingId, 
  hotelName, 
  description, 
  details, 
  priority,
  actions,
  checkboxes 
}) => {
  return (
    <div className="issue-item">
      <div className="issue-header">
        <div className="issue-id-group">
          <span className="issue-id">Issue ID: {issueId}</span>
          <a className="booking-id">{bookingId}</a>
          {priority && (
            <Tag className="priority-tag" color="error">
              {priority}
            </Tag>
          )}
        </div>
      </div>
      
      <h3 className="issue-hotel-name">{hotelName}</h3>
      <p className="issue-description">{description}</p>
      
      <div className="issue-details">{details}</div>
      
      <div className="issue-actions">
        <div className="action-buttons">
          {actions.map((action, idx) => (
            <Button
              key={idx}
              type={action.type || 'default'}
              danger={action.danger}
              icon={action.icon}
              size="large"
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          ))}
        </div>
        <Button size="large" className="assign-button">
          Assign to Team Member
        </Button>
      </div>
      
      {checkboxes && (
        <div className="issue-checkboxes">
          {checkboxes.map((checkbox, idx) => (
            <Checkbox key={idx}>{checkbox}</Checkbox>
          ))}
        </div>
      )}
    </div>
  );
};

// Reusable Issue Section Component
const IssueSection = ({ 
  title, 
  count, 
  variant, 
  issues, 
  defaultVisible = 3 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const visibleIssues = isExpanded ? issues : issues.slice(0, defaultVisible);
  const hasMore = issues.length > defaultVisible;

  const getIndicatorClass = () => {
    switch (variant) {
      case 'critical': return 'indicator-critical';
      case 'warning': return 'indicator-warning';
      case 'info': return 'indicator-info';
      default: return 'indicator-default';
    }
  };

  return (
    <div className="issue-section">
      <div className="section-header">
        <div className="section-title-group">
          <span className={`section-indicator ${getIndicatorClass()}`} />
          <h2 className="section-title">{title}</h2>
          <span className="section-count">{count} active</span>
        </div>
        {hasMore && (
          <Button 
            type="link" 
            onClick={() => setIsExpanded(!isExpanded)}
            className="view-all-button"
          >
            {isExpanded ? 'View Less' : 'View All'}
          </Button>
        )}
      </div>
      
      <div className="issues-list">
        {visibleIssues.map((issue, idx) => (
          <IssueItem key={idx} {...issue} />
        ))}
      </div>
    </div>
  );
};

// Reusable Stat Card Component
const StatCard = ({ title, value, subtitle, trend, variant = 'default' }) => {
  return (
    <div className={`stat-card stat-card-${variant}`}>
      <h4 className="stat-title">{title}</h4>
      <div className="stat-value">{value}</div>
      <div className="stat-footer">
        {trend && (
          <span className={`stat-trend ${trend.type}`}>{trend.value}</span>
        )}
        {subtitle && (
          <span className="stat-subtitle">{subtitle}</span>
        )}
      </div>
    </div>
  );
};

// Main Component
const Issues = () => {
  const [showAISuggestion, setShowAISuggestion] = useState(true);

  // Issue data
  const chargebackIssues = [
    {
      issueId: 'CB001',
      bookingId: 'BK12901',
      hotelName: 'Grand Plaza Hotel Dubai',
      description: 'Unauthorized amount - Charged USD 410 but only USD 340 authorized',
      details: 'VCC: VCC-2024-8734    TXN: TXN1029847561    Date: Dec 18, 2024    USD 340.00',
      actions: [
        { label: 'Initiate Chargeback', type: 'primary', danger: true, icon: <ExclamationCircleOutlined /> },
        { label: 'Approve Write-off' },
        { label: 'Investigate' },
      ],
      checkboxes: ['Assigned', 'Priority Set', 'Hotel Contacted', 'Resolution Tracked'],
    },
    {
      issueId: 'CB002',
      bookingId: 'BK12890',
      hotelName: 'Residence Inn Pittsburgh',
      description: 'Services not provided - Booking cancelled but amount not refunded',
      details: 'VCC: VCC-2024-8675    TXN: TXN1029847445    Date: Dec 12, 2024    USD 240.00',
      priority: 'High Priority',
      actions: [
        { label: 'Initiate Chargeback', type: 'primary', danger: true, icon: <ExclamationCircleOutlined /> },
        { label: 'Approve Write-off' },
        { label: 'Investigate' },
      ],
      checkboxes: ['Assigned', 'Priority Set', 'Hotel Contacted', 'Resolution Tracked'],
    },
    {
      issueId: 'CB003',
      bookingId: 'BK12888',
      hotelName: 'Marriott Hotel Boston',
      description: 'Double charge detected - Same amount charged twice',
      details: 'VCC: VCC-2024-8623    TXN: TXN1029847332    Date: Dec 15, 2024    USD 520.00',
      actions: [
        { label: 'Initiate Chargeback', type: 'primary', danger: true, icon: <ExclamationCircleOutlined /> },
        { label: 'Approve Write-off' },
        { label: 'Investigate' },
      ],
      checkboxes: ['Assigned', 'Priority Set', 'Hotel Contacted', 'Resolution Tracked'],
    },
    {
      issueId: 'CB004',
      bookingId: 'BK12887',
      hotelName: 'Hilton Garden Inn Chicago',
      description: 'Incorrect amount - Charged for extras not authorized',
      details: 'VCC: VCC-2024-8612    TXN: TXN1029847221    Date: Dec 14, 2024    USD 180.00',
      actions: [
        { label: 'Initiate Chargeback', type: 'primary', danger: true, icon: <ExclamationCircleOutlined /> },
        { label: 'Approve Write-off' },
        { label: 'Investigate' },
      ],
      checkboxes: ['Assigned', 'Priority Set', 'Hotel Contacted', 'Resolution Tracked'],
    },
    {
      issueId: 'CB005',
      bookingId: 'BK12886',
      hotelName: 'Hyatt Place Miami',
      description: 'Service dispute - Room quality not as promised',
      details: 'VCC: VCC-2024-8601    TXN: TXN1029847110    Date: Dec 13, 2024    USD 295.00',
      priority: 'High Priority',
      actions: [
        { label: 'Initiate Chargeback', type: 'primary', danger: true, icon: <ExclamationCircleOutlined /> },
        { label: 'Approve Write-off' },
        { label: 'Investigate' },
      ],
      checkboxes: ['Assigned', 'Priority Set', 'Hotel Contacted', 'Resolution Tracked'],
    },
    {
      issueId: 'CB006',
      bookingId: 'BK12884',
      hotelName: 'Best Western Seattle',
      description: 'Cancellation fee dispute - Policy not followed',
      details: 'VCC: VCC-2024-8590    TXN: TXN1029847009    Date: Dec 11, 2024    USD 160.00',
      actions: [
        { label: 'Initiate Chargeback', type: 'primary', danger: true, icon: <ExclamationCircleOutlined /> },
        { label: 'Approve Write-off' },
        { label: 'Investigate' },
      ],
      checkboxes: ['Assigned', 'Priority Set', 'Hotel Contacted', 'Resolution Tracked'],
    },
    {
      issueId: 'CB007',
      bookingId: 'BK12882',
      hotelName: 'Sheraton Denver',
      description: 'Overcharged - Rate mismatch from booking confirmation',
      details: 'VCC: VCC-2024-8579    TXN: TXN1029846998    Date: Dec 10, 2024    USD 380.00',
      actions: [
        { label: 'Initiate Chargeback', type: 'primary', danger: true, icon: <ExclamationCircleOutlined /> },
        { label: 'Approve Write-off' },
        { label: 'Investigate' },
      ],
      checkboxes: ['Assigned', 'Priority Set', 'Hotel Contacted', 'Resolution Tracked'],
    },
  ];

  const emailIssues = [
    {
      issueId: 'ISS001',
      bookingId: 'BK12892',
      hotelName: 'Holiday Inn Mumbai',
      description: 'Email bounced',
      details: 'Contact: reservations@holidayinn-old.com    Last attempt: 3 hours ago    Days pending: 1    ₹7,200',
      actions: [
        { label: 'Verify Email', type: 'primary', icon: <MailOutlined /> },
        { label: 'Call Hotel' },
        { label: 'Use Vendor Portal Link' },
      ],
      checkboxes: ['Assigned', 'Priority Set', 'Hotel Contacted', 'Resolution Tracked'],
    },
    {
      issueId: 'ISS002',
      bookingId: 'BK12891',
      hotelName: 'Radisson Blu Bangalore',
      description: 'Invalid email address',
      details: 'Contact: accounts@radisson-old.com    Last attempt: 5 hours ago    Days pending: 2    ₹9,500',
      actions: [
        { label: 'Verify Email', type: 'primary', icon: <MailOutlined /> },
        { label: 'Call Hotel' },
        { label: 'Use Vendor Portal Link' },
      ],
      checkboxes: ['Assigned', 'Priority Set', 'Hotel Contacted', 'Resolution Tracked'],
    },
    {
      issueId: 'ISS003',
      bookingId: 'BK12889',
      hotelName: 'ITC Grand Chennai',
      description: 'Email server timeout',
      details: 'Contact: finance@itc-chennai.com    Last attempt: 1 day ago    Days pending: 3    ₹12,800',
      priority: 'High Priority',
      actions: [
        { label: 'Verify Email', type: 'primary', icon: <MailOutlined /> },
        { label: 'Call Hotel' },
        { label: 'Use Vendor Portal Link' },
      ],
      checkboxes: ['Assigned', 'Priority Set', 'Hotel Contacted', 'Resolution Tracked'],
    },
    {
      issueId: 'ISS004',
      bookingId: 'BK12888',
      hotelName: 'Taj Hotel Delhi',
      description: 'Mailbox full',
      details: 'Contact: payments@tajhotel.com    Last attempt: 6 hours ago    Days pending: 1    ₹15,600',
      actions: [
        { label: 'Verify Email', type: 'primary', icon: <MailOutlined /> },
        { label: 'Call Hotel' },
        { label: 'Use Vendor Portal Link' },
      ],
      checkboxes: ['Assigned', 'Priority Set', 'Hotel Contacted', 'Resolution Tracked'],
    },
    {
      issueId: 'ISS005',
      bookingId: 'BK12887',
      hotelName: 'Oberoi Mumbai',
      description: 'Domain not found',
      details: 'Contact: reservations@oberoi-old.in    Last attempt: 2 days ago    Days pending: 4    ₹18,200',
      priority: 'High Priority',
      actions: [
        { label: 'Verify Email', type: 'primary', icon: <MailOutlined /> },
        { label: 'Call Hotel' },
        { label: 'Use Vendor Portal Link' },
      ],
      checkboxes: ['Assigned', 'Priority Set', 'Hotel Contacted', 'Resolution Tracked'],
    },
    {
      issueId: 'ISS006',
      bookingId: 'BK12886',
      hotelName: 'Leela Palace Bangalore',
      description: 'Email authentication failed',
      details: 'Contact: accounts@leela.com    Last attempt: 8 hours ago    Days pending: 2    ₹11,400',
      actions: [
        { label: 'Verify Email', type: 'primary', icon: <MailOutlined /> },
        { label: 'Call Hotel' },
        { label: 'Use Vendor Portal Link' },
      ],
      checkboxes: ['Assigned', 'Priority Set', 'Hotel Contacted', 'Resolution Tracked'],
    },
    {
      issueId: 'ISS007',
      bookingId: 'BK12885',
      hotelName: 'Hyatt Regency Kolkata',
      description: 'Spam filter blocking',
      details: 'Contact: finance@hyatt-kolkata.com    Last attempt: 4 hours ago    Days pending: 1    ₹8,900',
      actions: [
        { label: 'Verify Email', type: 'primary', icon: <MailOutlined /> },
        { label: 'Call Hotel' },
        { label: 'Use Vendor Portal Link' },
      ],
      checkboxes: ['Assigned', 'Priority Set', 'Hotel Contacted', 'Resolution Tracked'],
    },
    {
      issueId: 'ISS008',
      bookingId: 'BK12884',
      hotelName: 'Courtyard Marriott Pune',
      description: 'Auto-reply only, no delivery',
      details: 'Contact: reservations@marriott-pune.com    Last attempt: 10 hours ago    Days pending: 2    ₹13,500',
      actions: [
        { label: 'Verify Email', type: 'primary', icon: <MailOutlined /> },
        { label: 'Call Hotel' },
        { label: 'Use Vendor Portal Link' },
      ],
      checkboxes: ['Assigned', 'Priority Set', 'Hotel Contacted', 'Resolution Tracked'],
    },
  ];

  const expiredIssues = [
    {
      issueId: 'ISS101',
      bookingId: 'BK12885',
      hotelName: 'Four Points Delhi',
      description: 'VCC expired without charge',
      details: 'Contact: fourpoints@example.com    Last attempt: 2 days ago    Days pending: 5    ₹11,200',
      priority: 'High Priority',
      actions: [
        { label: 'Reissue VCC', type: 'primary', icon: <ClockCircleOutlined /> },
        { label: 'Switch to NEFT' },
        { label: 'Contact Hotel' },
      ],
      checkboxes: ['Assigned', 'Priority Set', 'Hotel Contacted', 'Resolution Tracked'],
    },
    {
      issueId: 'ISS102',
      bookingId: 'BK12883',
      hotelName: 'Novotel Goa',
      description: 'Hotel never charged VCC',
      details: 'Contact: goa@novotel.com    Last attempt: 3 days ago    Days pending: 8    ₹11,200',
      priority: 'High Priority',
      actions: [
        { label: 'Reissue VCC', type: 'primary', icon: <ClockCircleOutlined /> },
        { label: 'Switch to NEFT' },
        { label: 'Contact Hotel' },
      ],
      checkboxes: ['Assigned', 'Priority Set', 'Hotel Contacted', 'Resolution Tracked'],
    },
    {
      issueId: 'ISS103',
      bookingId: 'BK12881',
      hotelName: 'Ramada Jaipur',
      description: 'VCC expired before hotel could charge',
      details: 'Contact: payments@ramada-jaipur.com    Last attempt: 1 day ago    Days pending: 6    ₹9,800',
      actions: [
        { label: 'Reissue VCC', type: 'primary', icon: <ClockCircleOutlined /> },
        { label: 'Switch to NEFT' },
        { label: 'Contact Hotel' },
      ],
      checkboxes: ['Assigned', 'Priority Set', 'Hotel Contacted', 'Resolution Tracked'],
    },
    {
      issueId: 'ISS104',
      bookingId: 'BK12879',
      hotelName: 'Holiday Inn Express Ahmedabad',
      description: 'Technical issue prevented charge before expiry',
      details: 'Contact: finance@hiexpress-ahmd.com    Last attempt: 4 days ago    Days pending: 9    ₹14,300',
      priority: 'High Priority',
      actions: [
        { label: 'Reissue VCC', type: 'primary', icon: <ClockCircleOutlined /> },
        { label: 'Switch to NEFT' },
        { label: 'Contact Hotel' },
      ],
      checkboxes: ['Assigned', 'Priority Set', 'Hotel Contacted', 'Resolution Tracked'],
    },
    {
      issueId: 'ISS105',
      bookingId: 'BK12878',
      hotelName: 'Crowne Plaza Kochi',
      description: 'VCC details not received by hotel in time',
      details: 'Contact: reservations@crowneplaza-kochi.com    Last attempt: 2 days ago    Days pending: 7    ₹10,500',
      actions: [
        { label: 'Reissue VCC', type: 'primary', icon: <ClockCircleOutlined /> },
        { label: 'Switch to NEFT' },
        { label: 'Contact Hotel' },
      ],
      checkboxes: ['Assigned', 'Priority Set', 'Hotel Contacted', 'Resolution Tracked'],
    },
    {
      issueId: 'ISS106',
      bookingId: 'BK12877',
      hotelName: 'Aloft Bengaluru',
      description: 'System error - VCC expired during processing',
      details: 'Contact: accounts@aloft-bengaluru.com    Last attempt: 5 days ago    Days pending: 10    ₹16,700',
      priority: 'High Priority',
      actions: [
        { label: 'Reissue VCC', type: 'primary', icon: <ClockCircleOutlined /> },
        { label: 'Switch to NEFT' },
        { label: 'Contact Hotel' },
      ],
      checkboxes: ['Assigned', 'Priority Set', 'Hotel Contacted', 'Resolution Tracked'],
    },
  ];

  const doublePaymentIssues = [
    {
      issueId: 'ISS201',
      bookingId: 'BK12880',
      hotelName: 'Four Points Bangalore',
      description: 'Same booking paid via both VCC and NEFT',
      details: 'Contact: finance@fourpoints.com    Last attempt: 1 day ago    Days pending: 12    ₹7,800',
      priority: 'High Priority',
      actions: [
        { label: 'Initiate Recovery', type: 'primary', danger: true, icon: <ExclamationCircleOutlined /> },
        { label: 'Approve Write-off' },
        { label: 'Investigate' },
      ],
      checkboxes: ['Assigned', 'Priority Set', 'Hotel Contacted', 'Resolution Tracked'],
    },
    {
      issueId: 'ISS202',
      bookingId: 'BK12876',
      hotelName: 'Renaissance Mumbai',
      description: 'Duplicate payment detected - Both methods charged',
      details: 'Contact: payments@renaissance-mumbai.com    Last attempt: 2 days ago    Days pending: 8    ₹12,400',
      priority: 'High Priority',
      actions: [
        { label: 'Initiate Recovery', type: 'primary', danger: true, icon: <ExclamationCircleOutlined /> },
        { label: 'Approve Write-off' },
        { label: 'Investigate' },
      ],
      checkboxes: ['Assigned', 'Priority Set', 'Hotel Contacted', 'Resolution Tracked'],
    },
    {
      issueId: 'ISS203',
      bookingId: 'BK12875',
      hotelName: 'JW Marriott Delhi',
      description: 'Payment reconciliation error - Double payment confirmed',
      details: 'Contact: finance@jwmarriott-delhi.com    Last attempt: 3 days ago    Days pending: 15    ₹18,900',
      priority: 'High Priority',
      actions: [
        { label: 'Initiate Recovery', type: 'primary', danger: true, icon: <ExclamationCircleOutlined /> },
        { label: 'Approve Write-off' },
        { label: 'Investigate' },
      ],
      checkboxes: ['Assigned', 'Priority Set', 'Hotel Contacted', 'Resolution Tracked'],
    },
    {
      issueId: 'ISS204',
      bookingId: 'BK12874',
      hotelName: 'Westin Pune',
      description: 'Manual NEFT sent after VCC charge',
      details: 'Contact: accounts@westin-pune.com    Last attempt: 1 day ago    Days pending: 6    ₹9,600',
      actions: [
        { label: 'Initiate Recovery', type: 'primary', danger: true, icon: <ExclamationCircleOutlined /> },
        { label: 'Approve Write-off' },
        { label: 'Investigate' },
      ],
      checkboxes: ['Assigned', 'Priority Set', 'Hotel Contacted', 'Resolution Tracked'],
    },
  ];

  return (
    <div className="problem-resolution-container">
      {/* Header */}
      <div className="resolution-header">
        <h1 className="page-title">Problem Resolution Center</h1>
        <div className="header-filters">
          <Select defaultValue="all-issues" size="large" style={{ width: 200 }}>
            <Option value="all-issues">All Issues</Option>
            <Option value="critical">Critical Only</Option>
            <Option value="high-priority">High Priority</Option>
          </Select>
          <Select defaultValue="all-assignees" size="large" style={{ width: 200 }}>
            <Option value="all-assignees">All Assignees</Option>
            <Option value="me">Assigned to Me</Option>
            <Option value="unassigned">Unassigned</Option>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards-grid">
        <IssueSummaryCard
          title="Potential Chargebacks"
          count="7"
          subtitle="Active issues requiring attention"
          variant="critical"
        />
        <IssueSummaryCard
          title="Email Delivery Failures"
          count="8"
          subtitle="Active issues requiring attention"
          variant="warning"
        />
        <IssueSummaryCard
          title="Expired Uncharged VCCs"
          count="6"
          subtitle="Active issues requiring attention"
          variant="info"
        />
        <IssueSummaryCard
          title="Double Payment Recovery (VCC + NEFT)"
          count="1"
          subtitle="Active issues requiring attention"
          variant="critical"
        />
      </div>

      {/* AI Suggestion */}
      {showAISuggestion && (
        <AISuggestion
          confidence={92}
          message="We've detected <strong>hotel@olddomain.com</strong> consistently fails for Radisson Blu Jaipur. Records show <strong>newhotel@newdomain.com</strong> in recent correspondence."
          onUpdate={() => console.log('Update contact')}
          onDismiss={() => setShowAISuggestion(false)}
        />
      )}

      {/* Issue Sections */}
      <IssueSection
        title="Potential Chargebacks"
        count="7"
        variant="critical"
        issues={chargebackIssues}
        defaultVisible={3}
      />

      <IssueSection
        title="Email Delivery Failures"
        count="8"
        variant="warning"
        issues={emailIssues}
        defaultVisible={3}
      />

      <IssueSection
        title="Expired Uncharged VCCs"
        count="6"
        variant="info"
        issues={expiredIssues}
        defaultVisible={3}
      />

      <IssueSection
        title="Double Payment Recovery (VCC + NEFT)"
        count="4"
        variant="critical"
        issues={doublePaymentIssues}
        defaultVisible={3}
      />

      {/* Stats Cards */}
      <div className="stats-cards-grid">
        <StatCard
          title="Average Resolution Time"
          value="2.4 days"
          trend={{ value: '-0.6 days vs last month', type: 'positive' }}
        />
        <StatCard
          title="Issues Resolved This Month"
          value="127"
          subtitle="82% resolution rate"
        />
        <StatCard
          title="Amount Recovered"
          value="₹3.8L"
          subtitle="From duplicate charges"
          variant="success"
        />
      </div>
    </div>
  );
};

export default Issues;